import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';

/**
 * Interface that describes how an arbitrary stateful service can make its state
 * accessible via React context. Based on `useMutableSource`'s API:
 * https://github.com/bvaughn/rfcs/blob/useMutableSource/text/0000-use-mutable-source.md#public-api
 */
export interface IContextAdapter<ServiceType> {
  /**
   * The service's "version" can be anything, but this should return the same
   * (referentially equal) version if the service's state hasn't changed, and
   * should return a different version when the service's state has changed.
   */
  getVersion: (service: ServiceType) => unknown;
  /**
   * This should call the given callback function whenever the service's state
   * changes.
   */
  subscribe: (service: ServiceType, callback: () => void) => (() => void);
}

export interface IServiceContext<ServiceType> {
  Provider: React.FC<{ value: ServiceType }>;

  useService: () => ServiceType | undefined;

  useSelector: <Args extends unknown[], Return>(
    selectorFn: (service: ServiceType | undefined, ...args: Args) => Return,
    ...args: Args
  ) => Return;
}

/**
 * `createServiceContext()` allows you to access the state of a mutable source
 * of state (referred to as a "service" from here on), such as a redux store or
 * a `SubscribableState` instance, via React context.
 *
 * This is intended to allow writing and testing the logic outside of React,
 * while still being able to use it from React components easily.
 *
 * For this purpose, it is recommended to write a module that exports separate
 * functions that take the service instance as an argument and help access or
 * update the state. This approach has the added benefit of allowing more dead
 * code elimination via Webpack's tree shaking.
 *
 * To use the state from React components, you can use `useService` and
 * `useSelector` directly, and then pass the returned state to your helper
 * functions which abstract the logic.
 */
export default function createServiceContext<ServiceType>(
  { getVersion, subscribe }: IContextAdapter<ServiceType>,
): IServiceContext<ServiceType> {
  const ServiceVersionContext = React.createContext<unknown>(undefined);
  const ServiceContext = React.createContext<ServiceType | undefined>(undefined);

  /**
   * Returns the service instance. Does not re-render when the
   * service's state changes.
   *
   * Note that the passed service may be `undefined`, if a service instance
   * hasn't been provided via the returned `<Provider>` component.
   */
  const useService = (): ServiceType | undefined => useContext(ServiceContext);

  const noServiceVersion = Symbol();

  /**
   * Calls the given function with the given arguments, but passing the service
   * as the first argument. Causes a re-render whenever the service's state changes.
   *
   * Note that the passed service may be `undefined`, if a service instance
   * hasn't been provided via the returned `<Provider>` component.
   * `createServiceContext()` intentionally does not allow you to pass in a
   * default context value like `React.createContext()` does, because
   * `createServiceContext()` is specifically meant for stateful services, and
   * allowing you to specify a default service would encourage having global
   * state. Instead, in each selector function you can check for the `undefined`
   * case and return a default value for that selector.
   */
  const useSelector = <Args extends unknown[], Return>(
    selectorFn: (service: ServiceType | undefined, ...args: Args) => Return,
    ...args: Args
  ): Return => {
    // Used for side effect of causing re-renders when state changes.
    //
    // In the future, this will use useMutableSource to be compatible with
    // concurrent mode.
    useContext(ServiceVersionContext);
    const service = useService();
    const serviceVersion = service ? getVersion(service) : noServiceVersion;

    // We have to disable the linting rule in order to spread the `args` array
    // into the dependencies.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => selectorFn(service, ...args), [selectorFn, service, serviceVersion, ...args]);
  };

  const Provider: React.FC<{
    value: ServiceType,
  }> = ({
    children,
    value,
  }) => {
    const serviceVersion = useServiceVersion({ getVersion, subscribe }, value);

    return (
      <ServiceContext.Provider value={value}>
        <ServiceVersionContext.Provider value={serviceVersion}>
          {children}
        </ServiceVersionContext.Provider>
      </ServiceContext.Provider>
    );
  };

  return {
    Provider,
    useService,
    useSelector,
  };
}

/**
 * Returns current version of a service
 *
 * Can be used to rerender a hook / component for local (non-context)
 * services
 */
export function useServiceVersion<ServiceType>(
  { subscribe, getVersion }: IContextAdapter<ServiceType>,
  value: ServiceType,
) {
  const [serviceVersion, setServiceVersion] = useState(() => getVersion(value));

  useEffect(() => {
    const updateState = () => setServiceVersion(getVersion(value));

    const unsubscribe = subscribe(value, updateState);

    // The state might have changed between first render and first call to useEffect, before we
    // had a chance to call `.subscribe()`
    //
    // If the value hasn't changed, this will not cause a re-render
    updateState();

    return unsubscribe;
  }, [value, getVersion, subscribe]);

  return serviceVersion;
}

